import { useParams, useNavigate } from "react-router-dom";
import { useQuery, useMutation } from "@tanstack/react-query";
import { axiosInstance } from "../../../utils/axios";
import AdminSidebar from "../../../components/AdminSidebar";
import LoadingComponent from "../../../components/LoadingComponent";
import NoData from "../../../components/NoData";
import Button from "../../../components/Button";
import { useForm } from "react-hook-form";
import { toast } from "react-toastify";
import dayjs from "dayjs";
import { ArrowLeftIcon } from "@heroicons/react/20/solid";
import { useEffect } from "react";

export default function RentalEdit() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { register, handleSubmit, reset, watch } = useForm();

  const { data: rental, isLoading } = useQuery({
    queryKey: ["device-rental", id],
    queryFn: async () => {
      const response = await axiosInstance.get(
        `${window.REACT_APP_API_ENDPOINT}/device-rentals/${id}`
      );
      return response.data.data || response.data;
    },
  });

  useEffect(() => {
    if (rental) {
      reset({
        location: rental.location,
        email: rental.email,
        phone: rental.phone,
        monthly_rental_price: rental.monthly_rental_price,
        black_white_price: rental.black_white_price,
        color_price: rental.color_price,
        black_white_counter: rental.black_white_counter,
        color_counter: rental.color_counter,
        contract_start_date: rental.contract_start_date
          ? dayjs(rental.contract_start_date).format("YYYY-MM-DD")
          : "",
        expected_end_date: rental.expected_end_date
          ? dayjs(rental.expected_end_date).format("YYYY-MM-DD")
          : "",
        actual_end_date: rental.actual_end_date
          ? dayjs(rental.actual_end_date).format("YYYY-MM-DD")
          : "",
        last_date_of_counter_activation: rental.last_date_of_counter_activation
          ? dayjs(rental.last_date_of_counter_activation).format("YYYY-MM-DD")
          : "",
        status: rental.status,
      });
    }
  }, [rental, reset]);

  const updateMutation = useMutation({
    mutationFn: async (formData) => {
      const response = await axiosInstance.patch(
        `${window.REACT_APP_API_ENDPOINT}/device-rentals/${id}`,
        formData
      );
      return response.data;
    },
    onSuccess: () => {
      toast.success("Rental updated successfully");
      navigate(`/admin/rentals/${id}`, { replace: true });
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || "Failed to update rental");
    },
  });

  const onSubmit = (data) => {
    updateMutation.mutate(data);
  };

  if (isLoading) return <LoadingComponent />;
  if (!rental) return <NoData message="Rental not found" />;

  return (
    <AdminSidebar>
      <div className="px-4 sm:px-6 lg:px-8 bg-bgBlue min-h-screen">
        <div className="sm:flex flex-col sm:items-start">
          <div className="w-full flex justify-between py-4 px-4 bg-white rounded-lg mb-4">
            <div className="flex items-center gap-2">
              <Button
                variant="secondary"
                size="sm"
                icon={<ArrowLeftIcon className="h-4 w-4" />}
                onClick={() => navigate(-1)}
              >
                Back
              </Button>
              <h3 className="font-bold text-2xl">Edit Rental</h3>
            </div>
          </div>

          <form
            onSubmit={handleSubmit(onSubmit)}
            className="w-full bg-white rounded-lg p-6"
          >
            <div className="grid grid-cols-2 gap-6 mb-6">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Client
                </label>
                <input
                  type="text"
                  disabled
                  value={rental.client?.name || "---"}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-100 text-gray-600"
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Device Model
                </label>
                <input
                  type="text"
                  disabled
                  value={rental.device_model?.name || "---"}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-100 text-gray-600"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Location
                </label>
                <input
                  type="text"
                  {...register("location")}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Email
                </label>
                <input
                  type="email"
                  {...register("email")}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Phone
                </label>
                <input
                  type="tel"
                  {...register("phone")}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Monthly Rental Price
                </label>
                <input
                  type="number"
                  step="0.01"
                  {...register("monthly_rental_price")}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  B/W Price
                </label>
                <input
                  type="number"
                  step="0.01"
                  {...register("black_white_price")}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Color Price
                </label>
                <input
                  type="number"
                  step="0.01"
                  {...register("color_price")}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  B/W Counter
                </label>
                <input
                  type="number"
                  {...register("black_white_counter")}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Color Counter
                </label>
                <input
                  type="number"
                  {...register("color_counter")}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Contract Start Date
                </label>
                <input
                  type="date"
                  {...register("contract_start_date")}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Expected End Date
                </label>
                <input
                  type="date"
                  {...register("expected_end_date")}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Actual End Date
                </label>
                <input
                  type="date"
                  {...register("actual_end_date")}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Status
                </label>
                <select
                  {...register("status")}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">Select Status</option>
                  <option value="Active">Active</option>
                  <option value="Returned">Returned</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Last Date of Counter Activation
                </label>
                <input
                  type="date"
                  {...register("last_date_of_counter_activation")}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>

            <div className="flex gap-3">
              <Button type="submit" variant="primary" disabled={updateMutation.isPending}>
                {updateMutation.isPending ? "Saving..." : "Save Changes"}
              </Button>
              <Button
                type="button"
                variant="secondary"
                onClick={() => navigate(-1)}
              >
                Cancel
              </Button>
            </div>
          </form>
        </div>
      </div>
    </AdminSidebar>
  );
}
